<template lang="pug">
.briq-form-element
  BriqRadio(
    v-if="showSelectedInput"
    :label="label"
    :name="`${name}-dateRange`"
    radioButtonStyle="grey"
    :options="dateRangeOptions"
    :optionsDescription="dateRangeOptionsDescriptions"
    v-model="dateRange"
    :hide-optional-marker="true")
  .datepicker-holder(v-show="showCustomDatePicker")
    input(hidden name="period" v-model="period" v-if="showSelectedInput")
    input(
      type="text"
      name="reportDate"
      class="form-element flatpickr"
      :limit-date-range="customDateRangeLimit"
      v-model="flatpickrPeriod"
      placeholder="Select period"
      data-mode="range"
      data-show-months="1"
      :data-disabled-dates="customDatesDisabled"
      :data-max-date="maximumDate"
      autocomplete="off")
  
</template>

<script>
import { defaultDateRanges } from '../helpers.js'
import { subDays, subMonths, addDays, format, startOfMonth, endOfMonth } from 'date-fns'
import moment from 'moment'

export default {
  name: 'DateRangeSelector',
  props: {
    label: String,
    name: String,
    dateRangesAllowed: Object,
    maxDate: String,
    showSelectedInput: {
      type: Boolean,
      default: true,
    },
    customDateRangeLimit: {
      default: null,
      type: Number,
    },
    dateRangeDisabledDates: {
      default: null,
      type: Array
    },
  },
  data() {
    return {
      particle: 'to',
      showCustomDatePicker: false,
      dateRange: '',
      dateRangeOptions: { ...defaultDateRanges },
      dateRangeOptionsDescriptions: {},
      period: '',
      flatpickrPeriod: '',
    }
  },
  computed: {
    today () {
      return format(new Date(), this.$defaultDateFormat)
    },
    yesterday() {
      return format(subDays(new Date(), 1), this.$defaultDateFormat)
    },
    dayBeforeYesterday() {
      return format(subDays(new Date(), 2), this.$defaultDateFormat)
    },
    oneWeekAgo() {
      return format(subDays(new Date(), 7), this.$defaultDateFormat)
    },
    startOfPreviousMonth() {
      return format(startOfMonth(subMonths(new Date(), 1)), this.$defaultDateFormat)
    },
    endOfPreviousMonth() {
      return format(endOfMonth(subMonths(new Date(), 1)), this.$defaultDateFormat)
    },
    tomorrow() {
      return format(addDays(new Date(), 1), this.$defaultDateFormat)
    },
    oneWeekAhead() {
      return format(addDays(new Date(), 7), this.$defaultDateFormat)
    },
    maximumDate () {
      if (!this.maxDate) return null
      if (this.maxDate.includes('-')) return this.maxDate
      if (this.maxDate.includes('/')) return this.maxDate.replace('/', '-')
      return this[this.maxDate]
    },
    customDatesDisabled() {
      if (this.dateRangeDisabledDates && this.dateRangeDisabledDates.length > 0) {
        let dates = []
        this.dateRangeDisabledDates.forEach(date => {
          if (!date.includes('-')) {
            const getDate = this[date]
            dates.push(getDate)
          } else {
            dates.push(date)
          }
        })
        return dates.join(',')
      } else return null
    }
  },
  methods: {
    populateDateRangeFilterWithAllowedSelections() {
      const dateRangeOptions = this.dateRangeOptions

      Object.keys(this.dateRangeOptions).forEach(defaultDateRangeKey => {
        if (typeof this.dateRangesAllowed[defaultDateRangeKey] === 'undefined') {
          delete dateRangeOptions[defaultDateRangeKey]
        }
      })

      this.dateRangeOptions = dateRangeOptions
    },
    formatDate(dateString) {
      // todo: remove dependency on moment
      return moment(dateString, 'YYYY-MM-DD').format(
        moment.localeData(this.$locale).longDateFormat('LL'),
      )
    },
    formatDateRange(dateFrom, dateTo) {
      return `${this.formatDate(dateFrom)} ${this.particle} ${this.formatDate(dateTo)}`
    },
  },
  watch: {
    dateRange: function(newVal, oldVal) {
      if (newVal === 'custom') {
        this.showCustomDatePicker = true
        this.period = ''
      } else {
        this.showCustomDatePicker = false
      }

      switch (newVal) {
        case 'today':
          this.period = `${this.today}`
          break
        case 'yesterday':
          this.period = `${this.yesterday}`
          break
        case 'dayBeforeYesterday':
          this.period = `${this.dayBeforeYesterday}`
          break
        case 'lastSevenDays':
          this.period = `${this.oneWeekAgo} ${this.particle} ${this.yesterday}`
          break
        case 'previousMonth':
          this.period = `${this.startOfPreviousMonth} ${this.particle} ${this.endOfPreviousMonth}`
          break
        case 'tomorrow':
          this.period = `${this.tomorrow}`
          break
        case 'upcomingSevenDays':
          this.period = `${this.tomorrow} ${this.particle} ${this.oneWeekAhead}`
          break
        default:
          null
          break
      }
    },
    period: function(newVal) {
      this.$emit('periodSelected', this.period)
    },
    flatpickrPeriod: function(newVal) {
      this.period = newVal
    },
  },
  beforeMount() {
    this.period = `${this.yesterday}`

    this.dateRangeOptionsDescriptions.today = this.formatDate(this.today)
    this.dateRangeOptionsDescriptions.yesterday = this.formatDate(this.yesterday)
    this.dateRangeOptionsDescriptions.dayBeforeYesterday = this.formatDate(this.dayBeforeYesterday)
    this.dateRangeOptionsDescriptions.lastSevenDays = this.formatDateRange(
      this.oneWeekAgo,
      this.yesterday,
    )
    this.dateRangeOptionsDescriptions.previousMonth = this.formatDateRange(
      this.startOfPreviousMonth,
      this.endOfPreviousMonth,
    )
    this.dateRangeOptionsDescriptions.tomorrow = this.formatDate(this.tomorrow)
    this.dateRangeOptionsDescriptions.upcomingSevenDays = this.formatDateRange(
      this.tomorrow,
      this.oneWeekAhead,
    )
    this.populateDateRangeFilterWithAllowedSelections()
    this.dateRange = Object.keys(this.dateRangeOptions)[0]
  },
}
</script>

<style scoped lang="stylus">
.datepicker-holder
  position relative
  top -24px
  left 25px
  width 90%
</style>
